import type {
  Asset,
  AssetCollectionPicture,
  AssetKind,
  AssetMedia,
} from "@pax/api-contract/assets/v1alpha1";
import type { ImageSet } from "@pax/api-contract/common";
import { thumbHashToAverageRGBA, thumbHashToDataURL } from "thumbhash";

export const KIND_NAMES: Record<AssetKind, { one: string; many: string }> = {
  image: { one: "Image", many: "Images" },
  video: { one: "Video", many: "Videos" },
  audio: { one: "Audio", many: "Audio" },
  basemap: { one: "Basemap", many: "Basemaps" },
};

/** The picture that stands for an asset in a grid, with the shape it was made in. */
export interface AssetPicture {
  image: ImageSet;
  /** A JPEG of the same picture that never moves, for reduced motion. */
  still: string;
  width: number;
  height: number;
  animated: boolean;
  thumbhash: string;
}

export function pictureOf(media: AssetMedia | null): AssetPicture | null {
  if (!media) return null;
  switch (media.kind) {
    case "image":
      return {
        image: media.image,
        still: media.still.url,
        width: media.width,
        height: media.height,
        animated: media.animated,
        thumbhash: media.thumbhash,
      };
    case "video":
      return {
        image: media.poster,
        still: media.still.url,
        width: media.width,
        height: media.height,
        animated: false,
        thumbhash: media.thumbhash,
      };
    case "audio":
      return null;
  }
}

/** A picture on a collection's card, uncropped, in the same terms as an asset's own. */
export function collectionPictureOf(cover: AssetCollectionPicture): AssetPicture {
  const full = cover.image.sources.at(-1);
  return {
    image: cover.image,
    still: cover.still?.url ?? "",
    width: full?.width ?? 1,
    height: full?.height ?? 1,
    animated: cover.still !== null,
    thumbhash: cover.thumbhash,
  };
}

const placeholders = new Map<string, string>();

/**
 * The picture's colours at a few pixels, decoded once per hash. It is drawn
 * under the picture at the picture's own shape, so a grid arriving on a slow
 * connection is already the right mosaic in the right colours.
 */
export function placeholderOf(thumbhash: string): string {
  let url = placeholders.get(thumbhash);
  if (!url) {
    const bytes = Uint8Array.from(atob(thumbhash), character => character.charCodeAt(0));
    url = thumbHashToDataURL(bytes);
    placeholders.set(thumbhash, url);
  }
  return url;
}

/** A picture's one overall colour, as CSS, and how bright it is, from 0 for black to 1 for white. */
export interface AverageColour {
  colour: string;
  brightness: number;
}

const averages = new Map<string, AverageColour>();

/** The picture's overall colour, decoded once per hash. */
export function averageOf(thumbhash: string): AverageColour {
  let average = averages.get(thumbhash);
  if (!average) {
    const bytes = Uint8Array.from(atob(thumbhash), character => character.charCodeAt(0));
    const { r, g, b } = thumbHashToAverageRGBA(bytes);
    average = {
      colour: `rgb(${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)})`,
      brightness: 0.2126 * r + 0.7152 * g + 0.0722 * b,
    };
    averages.set(thumbhash, average);
  }
  return average;
}

/** A running time as a clock reads it: "0:08", "3:27". */
export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** How far into a recording, as a clock reads it: a second only once it has passed. */
export function formatElapsed(seconds: number): string {
  return formatDuration(Math.floor(seconds));
}

const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });

/** A count small enough to read at a glance: "8", "1.2k", "3.4m". */
export function formatCount(value: number): string {
  return compact.format(value).toLowerCase();
}

/** An asset's name, or what to call a draft that hasn't been given one. */
/**
 * What changes when a version's file does: the address of one of its files, which are named by
 * their bytes. Keyed on it, a player or a stage starts over for a new file.
 */
export function mediaKey(media: AssetMedia): string {
  switch (media.kind) {
    case "image":
    case "video":
      return media.still.url;
    case "audio":
      return media.url;
  }
}

/**
 * What to call the version shown, once the asset has more than one published version: its number
 * and label, or nothing while it has only one.
 */
export function versionName(
  asset: Pick<Asset, "publishedVersionCount" | "version" | "label">,
): string | null {
  if (asset.publishedVersionCount < 2 || asset.version === null) return null;
  return asset.label ? `Version ${asset.version} · ${asset.label}` : `Version ${asset.version}`;
}

export function titleOf(asset: Pick<Asset, "title" | "kind">): string {
  return asset.title ?? `Untitled ${KIND_NAMES[asset.kind].one.toLowerCase()}`;
}

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

export function languageName(code: string): string {
  if (code === "zxx") return "No words";
  return languageNames.of(code) ?? code;
}
